import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { NumericFormat } from 'react-number-format';
import {
  FormGroup,
  Input as BootstrapInput,
  InputGroupText,
  InputGroup,
  Label,
} from "reactstrap";

import './Input.scss';

/**
 * Redesigned Input component for creating form fields
 * 
 * @component
 * @example
 * <Input 
 *   type="text" 
 *   name="username" 
 *   label="Username" 
 *   value={username}
 *   onChange={handleChange}
 *   required
 * />
 */
export const Input = ({
  // Basic info
  type = "text",
  name,
  value,
  onChange,
  onBlur,
  
  // Appearance
  label,
  placeholder,
  className = "",
  
  // Icons and additional text
  icon,
  iconPosition = "prepend",
  inputText,
  inputTextPosition = "append",
  
  // States
  disabled = false,
  required = false,
  error = false,
  errorText = "",
  
  // Other
  children,
  multiple = false,
  ...rest
}) => {
  // Password field visibility management
  const [showPassword, setShowPassword] = useState(false);
  
  // Determine input type (consider password visibility)
  const inputType = type === "password" 
    ? (showPassword ? "text" : "password") 
    : type;
  
  // Class definitions
  const wrapperClasses = classNames(
    'input-wrapper',
    { 'has-error': error || errorText }
  );
  
  const inputClasses = classNames(
    'form-control',
    className,
    { 'is-invalid': error || errorText }
  );
  
  // Render different component based on type
  const renderInputElement = () => {
    // Select-tyyppinen input
    if (type === "select") {
      return (
        <BootstrapInput
          type={type}
          name={name}
          id={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          className={inputClasses}
          disabled={disabled}
          multiple={multiple}
          {...rest}
        >
          {children}
        </BootstrapInput>
      );
    }
    
    // Number-tyyppinen input
    if (type === "number") {
      return (
        <NumericFormat
          name={name}
          id={name}
          value={value}
          onValueChange={(values) => {
            if (onChange) {
              onChange({
                target: {
                  name,
                  value: values.floatValue
                }
              });
            }
          }}
          thousandSeparator=" "
          decimalSeparator="."
          valueIsNumericString
          className={inputClasses}
          placeholder={placeholder}
          disabled={disabled}
          allowNegative
          {...rest}
        />
      );
    }
    
    // Textarea-tyyppinen input
    if (type === "textarea") {
      return (
        <BootstrapInput
          type={type}
          name={name}
          id={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          className={inputClasses}
          placeholder={placeholder}
          disabled={disabled}
          {...rest}
        />
      );
    }
    
    // Tavallinen input
    return (
      <BootstrapInput
        type={inputType}
        name={name}
        id={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        className={inputClasses}
        placeholder={placeholder}
        disabled={disabled}
        {...rest}
      />
    );
  };
  
  // Simple input without additional elements
  if (!icon && !inputText && type !== "password") {
    return (
      <FormGroup className={wrapperClasses}>
        {label && (
          <Label 
            for={name} 
            className={required ? 'required' : ''}
          >
            {label}
          </Label>
        )}
        
        {renderInputElement()}
        
        {errorText && (
          <div className="invalid-feedback d-block">
            {errorText}
          </div>
        )}
      </FormGroup>
    );
  }
  
  // Input with additional elements (icons, text, password toggle)
  return (
    <FormGroup className={wrapperClasses}>
      {label && (
        <Label 
          for={name} 
          className={required ? 'required' : ''}
        >
          {label}
        </Label>
      )}
      
      <InputGroup>
        {/* Prepend icon or text */}
        {((icon && iconPosition === "prepend") || (inputText && inputTextPosition === "prepend")) && (
          <InputGroupText className={disabled ? "input-group-text-disabled" : ""}>
            {icon && iconPosition === "prepend" && <i className={icon} />}
            {inputText && inputTextPosition === "prepend" && inputText}
          </InputGroupText>
        )}
        
        {/* The actual input */}
        {renderInputElement()}
        
        {/* Append icon or text */}
        {((icon && iconPosition === "append") || (inputText && inputTextPosition === "append")) && (
          <InputGroupText className={disabled ? "input-group-text-disabled" : ""}>
            {icon && iconPosition === "append" && <i className={icon} />}
            {inputText && inputTextPosition === "append" && inputText}
            </InputGroupText>
        )}
        
        {/* Password visibility toggle */}
        {type === "password" && (
          <InputGroupText 
            onClick={() => setShowPassword(!showPassword)} 
            style={{ cursor: 'pointer' }}
          >
            <i className={`fas fa-eye${showPassword ? "" : "-slash"}`} />
          </InputGroupText>
        )}
      </InputGroup>
      
      {/* Error message */}
      {errorText && (
        <div className="invalid-feedback d-block">
          {errorText}
        </div>
      )}
    </FormGroup>
  );
};

Input.propTypes = {
  /** Input field type */
  type: PropTypes.oneOf([
    'text', 'email', 'password', 'number', 'date', 
    'datetime-local', 'select', 'textarea', 'file'
  ]),
  /** Input field name */
  name: PropTypes.string.isRequired,
  /** Input field value */
  value: PropTypes.any,
  /** Event handler when value changes */
  onChange: PropTypes.func,
  /** Event handler when focus leaves */
  onBlur: PropTypes.func,
  /** Field label */
  label: PropTypes.node,
  /** Placeholder text */
  placeholder: PropTypes.string,
  /** Additional classes */
  className: PropTypes.string,
  /** Icon next to input field */
  icon: PropTypes.string,
  /** Icon position (prepend/append) */
  iconPosition: PropTypes.oneOf(['prepend', 'append']),
  /** Text next to input field */
  inputText: PropTypes.node,
  /** Text position (prepend/append) */
  inputTextPosition: PropTypes.oneOf(['prepend', 'append']),
  /** Is field disabled */
  disabled: PropTypes.bool,
  /** Is field required */
  required: PropTypes.bool,
  /** Does field have error */
  error: PropTypes.bool,
  /** Error message */
  errorText: PropTypes.string,
  /** Select field options */
  children: PropTypes.node,
  /** Multi-select in select field */
  multiple: PropTypes.bool
};

